import { UnknownAction } from 'redux';
import { CompanyFiscalCal } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { KpiInfo } from '@blisspointmedia/bpm-types/dist/Kpis';
import { YOUTUBE } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { LagOption } from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { awaitJSON, ToolsLambdaFetch, YoutubePerformanceLambdaFetch } from '../utils/fetch-utils';
import { dispatchError } from './modals';
import { makeReducer } from './utils';

import { AppDispatch, RootState } from '.';

const ROOT = 'company';

export interface MobiusViewInfo {
  company: string;
  key: string;
  name: string;
  url: string;
}

export interface CustomDashboardInfo {
  name: string;
  url: string;
}

export interface CompanyInfo {
  agency: string;
  all_mobius_views?: MobiusViewInfo[];
  beeswax_advertiser_domain?: string;
  beeswax_default_click?: string;
  beeswax_iab_category?: string;
  cid: string;
  color?: string;
  commission?: number;
  competitors?: string[];
  defaultCrossChannelSource?: string; // TODO: Temporary until we decide where we will handle this configuration
  default_competitor?: string;
  disable_pages?: string[];
  experiments: string[];
  fiscal_cal: CompanyFiscalCal;
  groups: string[];
  initial_kpi: string;
  kpi_filter_order?: string[];
  kpis: KpiInfo[];
  media_types?: string[];
  min_impressions_dates?: string;
  tinuiti_vertical_category?: string;
  mobiusx_account_id?: string;
  amc_instance_id?: string;
  name_abbrev?: string;
  name: string;
  newton_project_slug?: string | null;
  optimization_cid?: string;
  pacing_client_facing?: boolean;
  dtc_mmm_run?: boolean;
  commerce_mmm_run?: boolean;
  page_access_settings?: Record<string, string>;
  parent_company?: string;
  spike_kpis?: string[];
  streaming_performance_default_kpi: string;
  streaming_performance_default_lag: LagOption;
  sub_companies?: { name: string; cid: string }[];
  cross_channel_kpis: string[];
  custom_dashboards: CustomDashboardInfo[];
  salesforce_ids: string[];
  currency_code: string;
}

interface CompanyReduxState {
  companies: Record<string, CompanyInfo | undefined>;
  current: string;
  filteredKpis: Record<string, 'fetching' | Record<string, boolean>>;
}

const SET_CURRENT_COMPANY = `${ROOT}/SET_CURRENT_COMPANY`;

interface SetCurrentCompanyActionType extends UnknownAction {
  type: string;
  company: string;
}

export const SetCompany = (company: string): SetCurrentCompanyActionType => ({
  type: SET_CURRENT_COMPANY,
  company
});

const SET_COMPANY_INFO = `${ROOT}/SET_COMPANY_INFO`;
interface SetCompanyInfoActionType extends UnknownAction {
  type: string;
  company: string;
  info: Omit<CompanyInfo, 'cid'>;
}

export const SetCompanyInfo = (company: string, info: CompanyInfo): SetCompanyInfoActionType => ({
  type: SET_COMPANY_INFO,
  company,
  info
});

const SET_FILTERED_KPIS = `${ROOT}/SET_FILTERED_KPIS`;
interface SetFilteredKpisActionType {
  type: string;
  company: string;
  filteredKpis: 'fetching' | Record<string, boolean>;
}

const SET_FISCAL_CAL = `${ROOT}/SET_FISCAL_CAL`;

interface SetFiscalCalActionType extends UnknownAction {
  type: string;
  company: string;
  fiscalCal: CompanyFiscalCal;
}

export const SetFiscalCal = (
  company: string,
  fiscalCal: CompanyFiscalCal
): SetFiscalCalActionType => ({
  type: SET_FISCAL_CAL,
  company,
  fiscalCal
});

export const fetchStreamingKpis =
  (
    company: string,
    prefix?: string,
    useTestParams?: boolean
  ): ((dispatch: AppDispatch, getState: () => RootState) => Promise<void>) =>
  async (dispatch, getState) => {
    const testParams = useTestParams ? { channel: prefix, pageID: prefix } : {};
    if (!filteredKpisSelector(getState())) {
      dispatch({
        type: SET_FILTERED_KPIS,
        company,
        filteredKpis: 'fetching'
      });
      try {
        const validKpis = await ToolsLambdaFetch('/geo_kpis', {
          params: { company, ...testParams }
        });
        const res = await awaitJSON(validKpis);
        dispatch({
          type: SET_FILTERED_KPIS,
          company,
          filteredKpis: res
        });
      } catch (e) {
        dispatchError(dispatch, {
          message: `Failed to fetch streaming kpi info. Error: ${e.message}`,
          reportError: e
        });
      }
    }
  };

export const fetchYoutubeKpis =
  (
    company: string,
    useTestParams?: boolean
  ): ((dispatch: AppDispatch, getState: () => RootState) => Promise<void>) =>
  async (dispatch, getState) => {
    const testParams = useTestParams ? { channel: YOUTUBE, pageID: YOUTUBE } : {};
    if (!filteredKpisSelector(getState())) {
      dispatch({
        type: SET_FILTERED_KPIS,
        company,
        filteredKpis: 'fetching'
      });

      try {
        const validKpis = await YoutubePerformanceLambdaFetch('/kpis', {
          params: { company, ...testParams }
        });
        const res = await awaitJSON(validKpis);
        dispatch({
          type: SET_FILTERED_KPIS,
          company,
          filteredKpis: res
        });
      } catch (e) {
        dispatchError(dispatch, {
          message: `Failed to fetch youtube kpi info. Error: ${e.message}`,
          reportError: e
        });
      }
    }
  };

export const currentCompanySelector = (state: RootState): string => state[ROOT].current;
export const companyInfoSelector =
  (company: string) =>
  (state: RootState): CompanyInfo | undefined =>
    state[ROOT].companies[company];

export const mediaTypesSelector =
  (company: string) =>
  (state: RootState): string[] =>
    state[ROOT].companies[company]?.media_types || [];
export const initialKpiSelector =
  (company: string) =>
  (state: RootState): string | undefined =>
    state[ROOT].companies[company]?.initial_kpi;

export const competitorsSelector =
  (company: string) =>
  (state: RootState): string[] =>
    state[ROOT].companies[company]?.competitors || [];

export const disablePagesSelector =
  (company: string) =>
  (state: RootState): string[] =>
    state[ROOT].companies[company]?.disable_pages || [];

export const filteredKpisSelector = (state: RootState): 'fetching' | Record<string, boolean> =>
  state[ROOT].filteredKpis[currentCompanySelector(state)];

const setCompanyInfo = (
  state: CompanyReduxState,
  { company, info }: SetCompanyInfoActionType
): CompanyReduxState => ({
  ...state,
  companies: {
    ...state.companies,
    [company]: {
      ...info,
      cid: company
    }
  }
});

const setCurrentCompany = (
  state: CompanyReduxState,
  { company }: SetCurrentCompanyActionType
): CompanyReduxState => ({
  ...state,
  current: company
});

const setFilteredKpis = (
  state: CompanyReduxState,
  { company, filteredKpis }: SetFilteredKpisActionType
): CompanyReduxState => ({
  ...state,
  filteredKpis: {
    ...state.filteredKpis,
    [company]: filteredKpis
  }
});

const setFiscalCalendar = (
  state: CompanyReduxState,
  { company, fiscalCal }: SetFiscalCalActionType
): CompanyReduxState => ({
  ...state,
  companies: {
    ...state.companies,
    [company]: {
      ...state.companies[company],
      fiscal_cal: fiscalCal
    } as CompanyInfo
  }
});

export default makeReducer<CompanyReduxState>(
  {
    current: '',
    companies: {},
    filteredKpis: {}
  },
  {
    [SET_COMPANY_INFO]: setCompanyInfo,
    [SET_CURRENT_COMPANY]: setCurrentCompany,
    [SET_FILTERED_KPIS]: setFilteredKpis,
    [SET_FISCAL_CAL]: setFiscalCalendar
  }
);
